<script setup>
import { ref, watch } from 'vue'
import _ from 'lodash'
import { useNotification } from '@kyvg/vue3-notification'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import ModalBox from './ModalBox.vue'
import ClipLoader from '@/components/ui/ClipLoader.vue'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'

const props = defineProps({
	modalId: {
		type: String,
		default: 'EditPhoneNumberModal',
	},
	modalSize: {
		type: String,
		default: 'sm',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	contact: {
		required: true,
	},
})

const emit = defineEmits(['user-contact-updated'])

const { notify } = useNotification()

const processing = ref(false)
const validationError = ref(false)
const errorMsg = ref('')
const phoneNumberInput = ref(null)
const phoneNumberValid = ref(false)
const phoneFormattedNumber = ref(null)
const contactId = ref(null)
const telOptions = {
	showDialCodeInList: true,
	showDialCodeInSelection: true,
	showFlags: true,
	showSearchBox: true,
}

const { isVisible, hide } = useLegacyModalBridge(() => props.modalId, {
	onShow: () => syncFromContact(),
	onHidden: () => {
		processing.value = false
		validationError.value = false
	},
})

const syncFromContact = () => {
	if (props.contact?.contact) {
		phoneNumberInput.value = props.contact.contact.phoneNumber
		contactId.value = props.contact._id
	}
}

watch(() => props.contact, syncFromContact, { deep: true })

const onValidate = (mobile) => {
	validationError.value = false
	if (props.contact && mobile.number === props.contact.contact?.phoneNumber) {
		phoneNumberValid.value = false
		return
	}
	if (!mobile.valid) {
		phoneNumberValid.value = false
		return
	}
	phoneFormattedNumber.value = mobile.number
	phoneNumberValid.value = true
}

const dismiss = () => {
	hide()
}

const onSave = async () => {
	processing.value = true
	try {
		const updatedContact = await UserService.updateUserContact(contactId.value, {
			phone: phoneFormattedNumber.value,
		}, true)
		emit('user-contact-updated', updatedContact)

		const updatedUser = _.assign({}, UserService.getUser(), {
			contact: { id: contactId.value, phoneNumber: phoneFormattedNumber.value },
		})
		localStorage.setItem('user', JSON.stringify(updatedUser))
		hide()
	} catch (e) {
		notify({ group: 'error', text: e?.message || 'Failed to update phone number' })
		errorMsg.value = e?.message || 'Failed to update phone number'
		validationError.value = true
		setTimeout(() => {
			validationError.value = false
		}, 5000)
	}
	processing.value = false
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<h4 class="text-s-xl mb-2">Update phone number</h4>
			<p class="text-s-s text-dark-8 mb-4">
				All streams have alert linked with this number will be updated too.
			</p>
			<vue-tel-input
				class="phone-input"
				v-model="phoneNumberInput"
				mode="national"
				auto-format
				:dropdown-options="telOptions"
				@validate="onValidate"
			/>
			<span v-if="validationError" class="text-s-s mt-2 block" style="color: #ba2929;">{{ errorMsg }}</span>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="secondary" size="md" @click="dismiss">
					Cancel
				</FormButton>
				<FormButton type="main" size="md" :disabled="!phoneNumberValid || processing" @click="onSave">
					Update Number
					<span v-if="processing" class="ml-2">
						<clip-loader color="#ffffff" size="15px" />
					</span>
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>

<style>
.country-selector__input {
	border: 0px solid #ccc !important;
}
.input-tel__input {
	border: 0px solid #ccc !important;
}
</style>
